import { useCallback, useEffect, useRef, useState } from "react";
import { buildLabel, dismissUpdated, snooze, updateNow, type UpdateView, useUpdateView } from "./update";

export const DONE_MS = 10_000;
export const NOTE_HEIGHT = "--update-note-h";

interface Root {
  style: Pick<CSSStyleDeclaration, "setProperty" | "removeProperty">;
}

/** Write the note height on the root while the note shows, so main keeps room below it (rule U-35). */
export function watchNoteHeight(note: Pick<HTMLElement, "getBoundingClientRect">, root: Root = document.documentElement): () => void {
  const write = () => root.style.setProperty(NOTE_HEIGHT, `${Math.ceil(note.getBoundingClientRect().height)}px`);
  write();
  const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(write);
  observer?.observe(note as Element);
  return () => {
    observer?.disconnect();
    root.style.removeProperty(NOTE_HEIGHT);
  };
}

function useNoteHeight() {
  const stop = useRef<(() => void) | null>(null);
  return useCallback((note: HTMLElement | null) => {
    stop.current?.();
    stop.current = note ? watchNoteHeight(note) : null;
  }, []);
}

/** "1:05" for 65 s. */
export function clockText(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export const holdText = (labels: string[]) => `Save or close ${labels.join(" and ")} to update.`;

/** The countdown for a screen reader: it changes each 30 s, then one time at 10 s, not each second. */
export function spokenCountdown(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  if (seconds <= 10) return "The app updates to the new version in 10 seconds or less.";
  return `The app updates to the new version in ${clockText(Math.ceil(seconds / 30) * 30_000)}.`;
}

interface Props {
  view: UpdateView;
  now: number;
  onNow: () => void;
  onSnooze: () => void;
  onDone: () => void;
  noteRef?: (note: HTMLElement | null) => void;
}

/** The note of a new version (rule U-35): a quiet note, a countdown, or a request to save a form. */
export function UpdateNoteView({ view, now, onNow, onSnooze, onDone, noteRef }: Props) {
  if (view.phase === "current") {
    if (!view.updatedFrom) return null;
    return (
      <p ref={noteRef} className="update-note" role="status" aria-live="polite" data-testid="update-done">
        Updated to version {buildLabel()}.{" "}
        <button type="button" onClick={onDone}>
          Close
        </button>
      </p>
    );
  }
  if (view.phase === "reloading") {
    return (
      <p ref={noteRef} className="update-note" role="status" aria-live="polite" data-testid="update-ready" data-phase="reloading">
        Updating…
      </p>
    );
  }
  const held = view.holds.length > 0;
  const counting = view.phase === "due" && view.countdownEnd !== null && !held;
  const text = counting
    ? `The app updates to the new version in ${clockText(view.countdownEnd! - now)}.`
    : held
      ? `A new version is ready. ${holdText(view.holds)}`
      : view.phase === "due"
        ? "A new version is ready. The app updates after the wait."
        : "A new version is ready.";
  return (
    <div ref={noteRef} className={`update-note${view.phase === "due" ? " due" : ""}`} data-testid="update-ready" data-phase={view.phase} data-held={held || undefined}>
      <span className="visually-hidden" role="status" aria-live="polite" data-testid="update-spoken">
        {counting ? spokenCountdown(view.countdownEnd! - now) : text}
      </span>
      <span aria-hidden="true" data-testid="update-text">
        {text}
      </span>
      {!held && (
        <button type="button" className="primary" data-testid="update-reload" onClick={onNow}>
          Update now
        </button>
      )}
      {counting && view.canSnooze && view.snoozesLeft > 0 && (
        <button type="button" data-testid="update-snooze" onClick={onSnooze}>
          Wait 30 min
        </button>
      )}
    </div>
  );
}

export function UpdateNote() {
  const view = useUpdateView();
  const [now, setNow] = useState(Date.now);
  const noteRef = useNoteHeight();
  const counting = view.phase === "due" && view.countdownEnd !== null;
  useEffect(() => {
    if (!counting) return;
    setNow(Date.now());
    const clock = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(clock);
  }, [counting]);
  useEffect(() => {
    if (!view.updatedFrom || view.phase !== "current") return;
    const hide = setTimeout(dismissUpdated, DONE_MS);
    return () => clearTimeout(hide);
  }, [view.updatedFrom, view.phase]);
  return <UpdateNoteView view={view} now={now} onNow={() => void updateNow()} onSnooze={() => void snooze()} onDone={dismissUpdated} noteRef={noteRef} />;
}
